import { useCallback } from 'react';
import {
  DEFAULT_CHAIN,
  SUPPORTED_CHAINS,
  USDC_DATA,
  USDT_DATA,
} from '@package/smart-wallet/constants';
import { useExecuteTransactions } from '@package/smart-wallet/hooks';
import { useSmartWallet } from '@package/smart-wallet/providers';
import {
  buildErc20TransferTransaction,
  buildNativeTransferTransaction,
  isEvmAddress,
  isSignerInitialized,
} from '@package/smart-wallet/utils';
import * as Sentry from '@sentry/react';
import { Address } from 'viem';
import { useAccount } from 'wagmi';

export const useWithdrawBalance = () => {
  const { chain } = useAccount();
  const { buildTransactions, executeTransactions } = useExecuteTransactions();
  const { smartAccountAddress, isLoading: isLoadingSmartWallet } =
    useSmartWallet();

  const chainId = chain?.id ?? DEFAULT_CHAIN.id;
  const currentChain = SUPPORTED_CHAINS[chainId];

  /* Callbacks */
  const handleWithdrawBalance = useCallback(
    async (tokenSymbol: string, amount: string, address: string) => {
      try {
        isSignerInitialized(smartAccountAddress ?? '');

        if (!isEvmAddress(address)) {
          throw new Error('Invalid address');
        }

        if (Number(amount) === 0) {
          throw new Error('Amount is 0');
        }

        // Determine token contract address and decimals based on symbol
        let tokenAddress: Address | undefined;
        let decimals = 18;
        let isNativeToken = false;

        // Normalize token symbol (remove special characters and numbers)
        const normalizedSymbol = tokenSymbol
          .replace(/[₮]/g, 'T') // Replace ₮ with T
          .replace(/0+$/, '') // Remove trailing zeros
          .toUpperCase()
          .trim();

        console.log('Withdrawing token:', {
          original: tokenSymbol,
          normalized: normalizedSymbol,
        });

        if (
          normalizedSymbol === 'USDT' ||
          normalizedSymbol.startsWith('USDT')
        ) {
          tokenAddress = (USDT_DATA as any)[chainId]?.contractAddress;
          decimals = (USDT_DATA as any)[chainId]?.decimals || 18;
        } else if (
          normalizedSymbol === 'USDC' ||
          normalizedSymbol.startsWith('USDC')
        ) {
          tokenAddress = (USDC_DATA as any)[chainId]?.contractAddress;
          decimals = (USDC_DATA as any)[chainId]?.decimals || 6;
        } else if (tokenSymbol === currentChain?.chain.nativeCurrency.symbol) {
          // Native token
          isNativeToken = true;
          decimals = currentChain?.chain.nativeCurrency.decimals || 18;
        } else {
          throw new Error(`Unsupported token: ${tokenSymbol}`);
        }

        // Build transaction based on token type
        const withdrawTransaction = isNativeToken
          ? buildNativeTransferTransaction(address as Address, amount, decimals)
          : buildErc20TransferTransaction(
              address as Address,
              tokenAddress!,
              amount,
              decimals,
            );

        const { transactions } = buildTransactions({
          transactions: [withdrawTransaction],
        });

        const txHash = await executeTransactions({
          transactions,
        });

        if (!txHash) {
          throw new Error('Transaction hash is undefined');
        }

        return txHash;
      } catch (error) {
        console.error('Error sending withdraw transaction: ', error);
        Sentry.captureException({
          message: 'Error sending withdraw transaction',
          extra: {
            error,
          },
        });
        throw new Error(`Error sending withdraw transaction: ${error}`);
      }
    },
    [
      smartAccountAddress,
      chainId,
      currentChain,
      buildTransactions,
      executeTransactions,
    ],
  );

  /* Return */
  return {
    isLoading: isLoadingSmartWallet,
    handleWithdrawBalance,
  };
};
